import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";

const COLS =
  "id, title, price, compare_price, images, seller_id, category_id, created_at, sellers(store_name, address), category:categories(name, slug, icon)";

async function fetchFresh({ categoryId, limit }) {
  let q = supabase
    .from("products")
    .select(COLS)
    .eq("status", "active")
    .is("deleted_at", null)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (categoryId && categoryId !== "all") q = q.eq("category_id", categoryId);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

async function fetchByIds(ids) {
  if (!ids.length) return [];
  const { data, error } = await supabase
    .from("products")
    .select(COLS)
    .in("id", ids)
    .eq("status", "active")
    .is("deleted_at", null);
  if (error) throw error;
  const byId = Object.fromEntries((data || []).map((p) => [p.id, p]));
  return ids.map((id) => byId[id]).filter(Boolean);
}

async function fetchTrending({ categoryId, limit }) {
  const { data: ids, error } = await supabase.rpc("get_trending_products", {
    limit_n: limit,
    category_filter: categoryId && categoryId !== "all" ? categoryId : null,
  });
  if (error) throw error;
  const rows = await fetchByIds((ids || []).map((r) => r.id));
  if (rows.length < limit) {
    const extras = await fetchFresh({ categoryId, limit: limit - rows.length });
    const seen = new Set(rows.map((r) => r.id));
    extras.forEach((p) => !seen.has(p.id) && rows.push(p));
  }
  return rows.slice(0, limit);
}

async function fetchForYou({ userId, categoryId, limit }) {
  if (!userId) return [];
  const { data: ids, error } = await supabase.rpc("get_for_you_products", {
    user_id: userId,
    limit_n: limit,
    category_filter: categoryId && categoryId !== "all" ? categoryId : null,
  });
  if (error) throw error;
  const rows = await fetchByIds((ids || []).map((r) => r.id));
  if (rows.length === 0) {
    return fetchTrending({ categoryId, limit });
  }
  return rows;
}

async function fetchSearchPage({ query, categoryId, sort, priceMin, priceMax, pageSize, offset }) {
  let q = supabase
    .from("products")
    .select(COLS, { count: "exact" })
    .eq("status", "active")
    .is("deleted_at", null);
  if (query && query.trim()) q = q.ilike("title", `%${query.trim()}%`);
  if (categoryId && categoryId !== "all") q = q.eq("category_id", categoryId);
  if (priceMin != null) q = q.gte("price", priceMin * 100);
  if (priceMax != null) q = q.lte("price", priceMax * 100);
  if (sort === "priceAsc") q = q.order("price", { ascending: true });
  else if (sort === "priceDesc") q = q.order("price", { ascending: false });
  else q = q.order("created_at", { ascending: false });
  q = q.range(offset, offset + pageSize - 1);
  const { data, error, count } = await q;
  if (error) throw error;
  return { rows: data || [], count: count ?? 0 };
}

export function useProducts({ source = "fresh", categoryId = "all", limit = 60, userId = null } = {}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["products", { source, categoryId, limit, userId }],
    staleTime: 2 * 60_000,
    queryFn: () => {
      if (source === "trending") return fetchTrending({ categoryId, limit });
      if (source === "for-you") return fetchForYou({ userId, categoryId, limit });
      return fetchFresh({ categoryId, limit });
    },
  });
  return { products: data || [], loading: isLoading, error: isError };
}

export function useInfiniteProducts({
  source = "search",
  query = "",
  categoryId = "all",
  sort = "newest",
  priceMin = null,
  priceMax = null,
  pageSize = 24,
  userId = null,
} = {}) {
  const key = ["products", "infinite", { source, query, categoryId, sort, priceMin, priceMax, pageSize, userId }];

  return useInfiniteQuery({
    queryKey: key,
    initialPageParam: 0,
    staleTime: 60_000,
    queryFn: async ({ pageParam }) => {
      if (source !== "search") {
        if (pageParam > 0) return { rows: [], nextOffset: null, total: 0 };
        let rows;
        if (source === "trending") rows = await fetchTrending({ categoryId, limit: 60 });
        else if (source === "for-you") rows = await fetchForYou({ userId, categoryId, limit: 60 });
        else rows = await fetchFresh({ categoryId, limit: 60 });
        if (query && query.trim()) {
          const qLower = query.toLowerCase();
          rows = rows.filter((p) => p.title?.toLowerCase().includes(qLower));
        }
        return { rows, nextOffset: null, total: rows.length };
      }
      const { rows, count } = await fetchSearchPage({
        query, categoryId, sort, priceMin, priceMax, pageSize, offset: pageParam,
      });
      const nextOffset = pageParam + rows.length < count ? pageParam + pageSize : null;
      return { rows, nextOffset, total: count };
    },
    getNextPageParam: (last) => last.nextOffset,
  });
}
